/**
 * One entry in the navigation rail.
 *
 * A custom control rather than a `Button`, because a Webwin Button has no colour of its own - its fill
 * and its ink come from the theme, which is right for a form and wrong here: the rail is dark in both
 * themes, so a themed button on it would be a light rectangle on a navy band. The OpenSilver version
 * reached the same place from the other direction, overriding Background, Foreground, BorderThickness,
 * Padding and HorizontalContentAlignment on every one of fourteen buttons.
 *
 * Painting it here also buys the two things that made those buttons awkward: a left-aligned label, and
 * a label whose colour can change - the Security entry turns amber while an account is locked out -
 * without touching a brush somebody else is holding.
 */
import {
  Control,
  insets,
  type Color,
  type ControlOptions,
  type RenderContext,
  type Size,
  type Theme
} from 'webwin';
import { crm } from '../theme/CrmTheme.js';
import { FONT } from './layout.js';

export interface NavButtonOptions extends ControlOptions {
  text?: string;
  /** Marks the entry whose page is showing. */
  selected?: boolean;
  /** Draws the label in the warning colour - the Security entry with a lockout outstanding. */
  attention?: boolean;
  onClick?: () => void;
}

const PADDING = insets(12, 10, 12, 10);

export class NavButton extends Control {
  #text: string;
  #onClick: (() => void) | null;
  selected: boolean;
  attention: boolean;

  constructor(options: NavButtonOptions = {}) {
    super({ height: 38, cursor: 'pointer', tabStop: true, ...options });
    this.#text = options.text ?? '';
    this.#onClick = options.onClick ?? null;
    this.selected = options.selected ?? false;
    this.attention = options.attention ?? false;

    if (this.#onClick) this.on('click', () => this.#onClick?.());

    // Enter and Space activate it, as they do every other focusable control in the library. The
    // handler is called directly rather than by synthesizing a click event - a fabricated
    // MouseEventArgs would be a lie about where the pointer was.
    this.on('keydown', e => {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      e.handled = true;
      this.#onClick?.();
    });
  }

  get text(): string {
    return this.#text;
  }
  set text(value: string) {
    if (this.#text === value) return;
    this.#text = value;
    this.invalidate();
  }

  /** Fills the rail's width and keeps its own height, so a docking or stacking parent can place it. */
  override preferredSize(available: Size): Size {
    return { width: available.width, height: this.height };
  }

  protected override onPaint(rc: RenderContext, theme: Theme): void {
    const c = crm(theme);
    const r = this.localRect;
    const radius = theme.metrics.controlCornerRadius;

    // The rail is dark in both themes, so hover and selection are lightenings of it rather than
    // anything from the theme's control ramp - which is built for a card on a page.
    if (this.pressed) rc.fillRect(r, WHITE_12, radius);
    else if (this.selected) rc.fillRect(r, WHITE_10, radius);
    else if (this.hovered) rc.fillRect(r, WHITE_06, radius);

    // A 3px accent rail on the selected entry: the "where am I" signal that survives being the only
    // light thing on a dark band, and the one the device page's tab strip uses too.
    if (this.selected) {
      rc.fillRect({ x: r.x, y: r.y + 6, width: 3, height: Math.max(0, r.height - 12) }, c.brand, 2);
    }

    const ink: Color = this.attention ? c.warn : this.selected ? WHITE : c.sidebarText;
    rc.drawText(this.#text, r.x + PADDING.left, r.y + r.height / 2, this.selected ? FONT.bold : FONT.body, ink, {
      baseline: 'middle',
      maxWidth: Math.max(0, r.width - PADDING.left - PADDING.right)
    });
  }
}

const WHITE: Color = { r: 1, g: 1, b: 1, a: 1 };
const WHITE_06: Color = { r: 1, g: 1, b: 1, a: 0.06 };
const WHITE_10: Color = { r: 1, g: 1, b: 1, a: 0.1 };
const WHITE_12: Color = { r: 1, g: 1, b: 1, a: 0.12 };
